import React, { Component } from 'react';
import server from '../../api/server';

// Full-page privacy & consent gate. Shown before the dashboard when the customer
// needs to (re-)consent. Styled to match the modern client dashboard: #F5F6FA
// background, white rounded-2xl cards, #5B3FE0 purple accent, Poppins (inherited
// from the dashboard's .lg-modern-page body class).
//
// Loads the active consent items from the server (single source of truth — the
// same content snapshotted on submit), requires the mandatory policy/disclosure
// acknowledgements, and captures an explicit YES/NO for the Equifax credit check
// (either answer lets the customer proceed).
class ConsentPage extends Component {
  constructor(props) {
    super(props);

    this.state = {
      policies: [],
      reConsentMonths: 3,
      acks: {},            // { [policyKey]: true } for mandatory policy/disclosure
      creditCheck: null,   // "yes" | "no" | null
      loadingPolicies: true,
      submitting: false,
      error: null
    };
  }

  componentDidMount() {
    this.loadPolicies();
  }

  loadPolicies = async () => {
    try {
      const response = await server.post('/consent/policies');
      const policies = response.data.policies || [];
      this.setState({
        policies,
        reConsentMonths: response.data.re_consent_months || 3,
        loadingPolicies: false
      });
    } catch (error) {
      console.error('Failed to load consent policies:', error);
      this.setState({ loadingPolicies: false, error: 'Failed to load consent details. Please refresh and try again.' });
    }
  };

  toggleAck = (key) => {
    this.setState((prev) => ({ acks: { ...prev.acks, [key]: !prev.acks[key] }, error: null }));
  };

  setCreditCheck = (value) => {
    this.setState({ creditCheck: value, error: null });
  };

  mandatoryPolicies = () => this.state.policies.filter((p) => p.type !== 'credit_check');

  creditCheckPolicy = () => this.state.policies.find((p) => p.type === 'credit_check') || null;

  allMandatoryAcked = () => {
    const mandatory = this.mandatoryPolicies();
    return mandatory.length > 0 && mandatory.every((p) => this.state.acks[p.key] === true);
  };

  readyToSubmit = () => this.allMandatoryAcked() && (this.state.creditCheck === 'yes' || this.state.creditCheck === 'no');

  handleSubmit = async () => {
    if (!this.readyToSubmit()) {
      this.setState({ error: 'Please acknowledge each policy and choose Yes or No for the credit check.' });
      return;
    }

    this.setState({ submitting: true, error: null });

    const acknowledgements = {};
    this.mandatoryPolicies().forEach((p) => { acknowledgements[p.key] = true; });

    try {
      const response = await server.post('/consent/submit', {
        acknowledgements,
        credit_check: this.state.creditCheck
      });

      if (response.data.success) {
        this.props.onConsentSubmitted();
      } else {
        this.setState({ error: 'Failed to submit consents. Please try again.' });
      }
    } catch (error) {
      console.error('Consent submission error:', error);
      this.setState({
        error: error.response?.data?.error || 'Failed to submit consents. Please try again.'
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  // Card for a mandatory policy/disclosure item.
  renderPolicyCard = (policy) => {
    const checked = this.state.acks[policy.key] === true;
    const isDisclosure = policy.type === 'disclosure';
    const href = policy.view_route || policy.url || null;
    const external = !!policy.url && !policy.view_route;

    return (
      <button
        key={policy.key}
        type="button"
        onClick={() => this.toggleAck(policy.key)}
        className="group w-full text-left rounded-2xl bg-white transition-all duration-150 focus:outline-none"
        style={{
          border: `1.5px solid ${checked ? '#5B3FE0' : '#E5E7EB'}`,
          boxShadow: checked ? '0 6px 18px rgba(91,63,224,0.12)' : '0 1px 2px rgba(16,24,40,0.04)',
          padding: '20px'
        }}
      >
        <div className="flex items-start" style={{ gap: '16px' }}>
          <span
            className="flex items-center justify-center rounded-md transition-all duration-150"
            style={{
              width: '24px',
              height: '24px',
              marginTop: '2px',
              flexShrink: 0,
              backgroundColor: checked ? '#5B3FE0' : '#FFFFFF',
              border: `2px solid ${checked ? '#5B3FE0' : '#CBD2D9'}`
            }}
          >
            {checked && (
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                <path d="M5 10.5l3.2 3.2L15 7" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            )}
          </span>
          <span className="flex-1 block">
            <span className="block text-base font-bold" style={{ color: '#1F2937' }}>{policy.title}</span>
            {isDisclosure && policy.content ? (
              <span className="block mt-1 text-[13px] leading-relaxed" style={{ color: '#4B5563' }}>{policy.content}</span>
            ) : (
              <span className="block mt-1 text-sm leading-snug" style={{ color: '#6B7280' }}>
                Tap anywhere to acknowledge you have read and agree to this policy.
              </span>
            )}
            {href && (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="inline-flex items-center mt-2 text-[13px] font-semibold hover:underline"
                style={{ color: '#5B3FE0' }}
              >
                {external ? 'View policy (opens Mortgage Choice)' : 'View privacy policy'}
                <svg className="ml-1" width="13" height="13" viewBox="0 0 20 20" fill="none">
                  <path d="M7 5h8v8M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </a>
            )}
          </span>
        </div>
      </button>
    );
  };

  // Separate boxed section for the credit check YES/NO.
  renderCreditCheck = () => {
    const policy = this.creditCheckPolicy();
    if (!policy) return null;
    const { creditCheck } = this.state;
    const answered = creditCheck === 'yes' || creditCheck === 'no';

    const optionButton = (value, label) => {
      const active = creditCheck === value;
      const accent = value === 'yes' ? '#059669' : '#DC2626';
      return (
        <button
          type="button"
          onClick={() => this.setCreditCheck(value)}
          className="flex-1 rounded-xl transition-all duration-150 focus:outline-none"
          style={{
            padding: '13px 16px',
            fontSize: '15px',
            fontWeight: 700,
            cursor: 'pointer',
            color: active ? '#FFFFFF' : '#374151',
            backgroundColor: active ? accent : '#FFFFFF',
            border: `1.5px solid ${active ? accent : '#D1D5DB'}`,
            boxShadow: active ? '0 6px 16px rgba(0,0,0,0.10)' : 'none'
          }}
        >
          {label}
        </button>
      );
    };

    return (
      <div
        className="rounded-2xl bg-white"
        style={{
          border: `1.5px solid ${answered ? '#5B3FE0' : '#E5E7EB'}`,
          boxShadow: answered ? '0 6px 18px rgba(91,63,224,0.12)' : '0 1px 2px rgba(16,24,40,0.04)',
          padding: '20px'
        }}
      >
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center justify-center rounded-md"
            style={{ width: '26px', height: '26px', backgroundColor: '#EEF0FF' }}
          >
            <svg width="15" height="15" viewBox="0 0 20 20" fill="none">
              <path d="M3 7h14M3 7a2 2 0 012-2h10a2 2 0 012 2M3 7v6a2 2 0 002 2h10a2 2 0 002-2V7M6 12h3" stroke="#5B3FE0" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          <h3 className="text-base font-bold" style={{ color: '#1F2937' }}>{policy.title}</h3>
        </div>
        <p className="mt-2 text-[13px] leading-relaxed" style={{ color: '#4B5563' }}>{policy.content}</p>
        <div className="flex mt-4" style={{ gap: '12px' }}>
          {optionButton('yes', 'Yes, I consent')}
          {optionButton('no', 'No, I decline')}
        </div>
        <p className="mt-2 mb-0 text-xs" style={{ color: '#9CA3AF' }}>
          You can continue either way. Your choice is recorded for your file.
        </p>
      </div>
    );
  };

  render() {
    const { loadingPolicies, submitting, error, reConsentMonths } = this.state;
    const ready = this.readyToSubmit();

    return (
      <div className="min-h-screen w-full" style={{ backgroundColor: '#F5F6FA' }}>
        <div className="px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <div className="max-w-2xl mx-auto">
            {/* Logo */}
            <div className="flex justify-center mb-8 sm:mb-10">
              <img
                src={process.env.PUBLIC_URL + '/images/logos/dark_transparent.png'}
                alt="LoanGateway"
                className="h-8 sm:h-9 w-auto"
              />
            </div>

            {/* Intro */}
            <p className="text-xs sm:text-sm font-semibold tracking-wide uppercase" style={{ color: '#5B3FE0' }}>
              Before you continue
            </p>
            <h1 className="mt-2 text-2xl sm:text-3xl font-bold" style={{ color: '#1F2937' }}>
              Privacy &amp; Consent
            </h1>
            <p className="mt-2 text-sm sm:text-base leading-relaxed" style={{ color: '#6B7280' }}>
              Please review and agree to each of the items below to access your dashboard.
              These consents are valid for {reConsentMonths} months.
            </p>

            {/* Items */}
            {loadingPolicies ? (
              <div className="mt-10 flex flex-col items-center justify-center py-10">
                <span
                  className="inline-block h-6 w-6 rounded-full border-2 animate-spin"
                  style={{ borderColor: '#E5E7EB', borderTopColor: '#7C5CFC' }}
                />
                <p className="mt-3 text-sm" style={{ color: '#6B7280' }}>Loading consent details…</p>
              </div>
            ) : (
              <div className="mt-8 space-y-3">
                {this.mandatoryPolicies().map((p) => this.renderPolicyCard(p))}
                {this.renderCreditCheck()}
              </div>
            )}

            {/* Error */}
            {error && (
              <div
                className="mt-5 rounded-xl"
                style={{ padding: '12px 16px', backgroundColor: '#FEF2F2', border: '1px solid #FECACA' }}
              >
                <p className="mb-0 text-[13px]" style={{ color: '#DC2626' }}>{error}</p>
              </div>
            )}

            {/* Footer / submit */}
            {!loadingPolicies && (
              <div className="mt-8">
                <div className="flex items-center justify-center gap-2 mb-3">
                  <span
                    className="inline-block h-1.5 w-1.5 rounded-full"
                    style={{ backgroundColor: ready ? '#10B981' : '#D1D5DB' }}
                  />
                  <p className="mb-0 text-[13px] font-semibold" style={{ color: ready ? '#065F46' : '#9CA3AF' }}>
                    {ready
                      ? 'All set, ready to continue'
                      : 'Acknowledge each policy and answer the credit check to continue'}
                  </p>
                </div>

                <button
                  onClick={this.handleSubmit}
                  disabled={!ready || submitting}
                  className="w-full rounded-xl transition-all duration-150"
                  style={{
                    padding: '15px 24px',
                    fontSize: '16px',
                    fontWeight: 700,
                    color: '#FFFFFF',
                    border: 'none',
                    cursor: ready && !submitting ? 'pointer' : 'not-allowed',
                    backgroundColor: ready && !submitting ? '#5B3FE0' : '#C7CBD6',
                    boxShadow: ready && !submitting ? '0 10px 24px rgba(91,63,224,0.28)' : 'none'
                  }}
                >
                  {submitting ? 'Submitting…' : 'Continue to Dashboard'}
                </button>

                <p className="mt-4 text-center text-xs" style={{ color: '#9CA3AF' }}>
                  Managed by CBD Mortgage Advisors Pty Ltd · Your responses are securely recorded.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

export default ConsentPage;
